import { jsx as _jsx } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2020 semantics GmbH
//  Date      : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156393 $
//  Purpose   : webcache components
// =============================================================================
// ---Third-party imports
import React from 'react';
import { Icon, Button } from 'semantic-ui-react';
// ---Framework imports
import log from 'uix/logging';
import { Actions, Sel } from 'uix/registries';
import { dict, size, sorted } from 'ldash';
import { useState, useMemo, useApi, useSelector, useActions } from 'uix/hooks';
import { prettyBytes } from 'uix/utils';
import { TranslatedTable } from 'uix/components';
// ---Exports
export const WebcacheTable = ({ otid, compact = false })=>{
    /* build table for webcache sizes
   */ let apiurl = useSelector(Sel.apiurl);
    let callApi = useActions('callApi');
    let { data, loading, reload } = useApi('filter/getWebcacheInfo', {
        id: otid
    }, {
        cached: true,
        autoreset: false
    });
    //~ vl.pprint(data)
    let { data: archiveData } = useApi('filter/getArchiveInfo', {
        id: otid
    }, {
        cached: true
    });
    let iconfig = useSelector(Sel.iconfig);
    // vl.pprint(iconfig)
    let orgWidth = data?.webcache.width;
    let dpi = archiveData?.archive?.dpix || 0;
    let button = (label, api_call, api_params)=>{
        let onClick = ()=>callApi(api_call, api_params, reload);
        return /*#__PURE__*/ _jsx(Button, {
            basic: true,
            fluid: true,
            size: "small",
            onClick: onClick,
            children: label
        }, api_call);
    };
    let widthLink = ({ value, original })=>/*#__PURE__*/ _jsx("a", {
            target: `wc_${value}`,
            href: apiurl.href(`/download/webcache/${value}/${otid}`),
            children: value
        });
    let availabe_wc = iconfig.webcache.sizes.filter((w)=>w <= Math.max(2000, orgWidth || 2000));
    let columns = [
        {
            Header: 'Width',
            accessor: 'name',
            Cell: widthLink
        },
        {
            Header: 'Size',
            accessor: 'width',
            Cell: ({ value, original })=>!value ? null : `${value}x${original.height}`
        },
        {
            Header: 'DPI',
            id: 'dpi',
            accessor: (r)=>!r.width ? null : parseInt(dpi * r.width / orgWidth)
        },
        {
            Header: 'Size',
            accessor: 'size',
            Cell: ({ value })=>value ? prettyBytes(value) : null
        }
    ];
    let BoolCell = ({ value })=>value ? /*#__PURE__*/ _jsx(Icon, {
            name: "check"
        }) : null;
    if (!compact) {
        columns = [
            {
                Header: 'Width',
                accessor: 'name',
                width: 80,
                Cell: widthLink
            },
            {
                Header: 'Path',
                accessor: 'path'
            },
            {
                Header: 'Reg',
                accessor: 'registered',
                width: 50,
                Cell: BoolCell
            },
            {
                Header: 'Exist',
                accessor: 'exists_fs',
                width: 50,
                Cell: BoolCell
            },
            {
                Header: 'Valid',
                accessor: 'valid',
                width: 50,
                Cell: BoolCell
            },
            {
                Header: 'Type',
                accessor: 'content_type',
                width: 50
            },
            {
                Header: 'Dimensions',
                accessor: 'width',
                Cell: ({ value, original })=>!value ? null : `${value}x${original.height}`,
                width: 100
            },
            {
                Header: 'DPI',
                id: 'dpi',
                accessor: (r)=>!r.width ? null : parseInt(dpi * r.width / orgWidth),
                width: 50
            },
            {
                Header: 'Size',
                accessor: 'size',
                Cell: ({ value })=>value ? prettyBytes(value) : null,
                width: 80
            },
            {
                Header: 'Created',
                accessor: 'fmt_ts',
                maxWidth: 190
            },
            {
                Header: 'Action',
                id: 'actions',
                accessor: (r)=>r.registered,
                Cell: ({ value: registered, original })=>{
                    let width = original.name;
                    return registered ? button('Remove', 'removeSingleWebcacheWidth', {
                        otid,
                        width
                    }) : button('Build', 'buildSingleWebcacheWidth', {
                        otid,
                        width
                    });
                },
                maxWidth: 90
            }
        ];
    }
    let rows = useMemo(()=>{
        let rows = data?.webcache.sizes;
        if (!rows) {
            rows = availabe_wc.map((w)=>({
                    name: w
                }));
        }
        return sorted(rows, (e)=>e.name == 0 ? orgWidth : parseInt(e.name));
    }, [
        data
    ]);
    return /*#__PURE__*/ _jsx(TranslatedTable, {
        data: rows,
        columns: columns,
        getTrProps: (state, rowInfo, column)=>({
                style: {
                    color: rowInfo && !rowInfo.row.width ? '#CCC' : undefined
                }
            })
    });
};
